import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import toast from 'react-hot-toast';
import { Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const [form, setForm]         = useState({ username: '', password: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading]   = useState(false);
  const { login }               = useAuth();
  const navigate                = useNavigate();
  const { executeRecaptcha }    = useGoogleReCaptcha();

  const set = (field) => (e) => setForm(p => ({ ...p, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username.trim() || !form.password) {
      toast.error('Completá todos los campos');
      return;
    }
    setLoading(true);
    try {
      const token = await executeRecaptcha('login');
      await login(form.username.trim(), form.password, token);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err?.message || 'Credenciales incorrectas');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface dark:bg-slate-950 flex items-center justify-center p-4">

      {/* Decoración de fondo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary-100/50 dark:bg-primary-500/10 rounded-full blur-3xl translate-x-1/3 -translate-y-1/3" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-primary-200/30 dark:bg-primary-500/5 rounded-full blur-3xl -translate-x-1/3 translate-y-1/3" />
      </div>

      <div className="w-full max-w-sm relative">

        {/* Arecore branding */}
        <div className="flex items-center gap-2.5 mb-8">
          <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center shrink-0">
            <svg width="14" height="14" viewBox="0 0 13 13" fill="none" aria-hidden="true">
              <rect x="1"   y="1"   width="4.5" height="4.5" rx="1.2" fill="white" fillOpacity="0.95" />
              <rect x="7.5" y="1"   width="4.5" height="4.5" rx="1.2" fill="white" fillOpacity="0.40" />
              <rect x="1"   y="7.5" width="4.5" height="4.5" rx="1.2" fill="white" fillOpacity="0.40" />
              <rect x="7.5" y="7.5" width="4.5" height="4.5" rx="1.2" fill="white" fillOpacity="0.95" />
            </svg>
          </div>
          <div>
            <p className="text-slate-900 dark:text-white font-bold text-base leading-none tracking-tight">Arecore</p>
            <p className="text-primary-400 text-[10px] font-bold uppercase tracking-widest mt-[3px] leading-none">ERP</p>
          </div>
        </div>

        {/* Card de login */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-card-md dark:shadow-none p-8">

          <div className="mb-7">
            <h1 className="text-lg font-bold text-slate-900 dark:text-white tracking-tight">Bienvenido</h1>
            <p className="text-sm text-slate-400 mt-1">Ingresá tus credenciales para continuar</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="username">Usuario</label>
              <input
                id="username"
                type="text"
                className="input"
                placeholder="Tu usuario"
                value={form.username}
                onChange={set('username')}
                disabled={loading}
                autoComplete="username"
                autoFocus
              />
            </div>

            <div>
              <label htmlFor="password">Contraseña</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPass ? 'text' : 'password'}
                  className="input pr-10"
                  placeholder="••••••••"
                  value={form.password}
                  onChange={set('password')}
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPass(p => !p)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors"
                  tabIndex={-1}
                >
                  {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className="btn-primary btn-lg w-full mt-2"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Ingresando...
                </>
              ) : (
                'Ingresar'
              )}
            </button>

            <p className="text-center text-[10px] text-slate-300 dark:text-slate-600 mt-1 leading-relaxed">
              Protegido por reCAPTCHA ·{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline hover:text-slate-400">Privacidad</a>
              {' '}y{' '}
              <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline hover:text-slate-400">Términos</a>
            </p>
          </form>

        </div>

        {/* Powered by Arecotech con logo */}
        <div className="flex items-center justify-center gap-2.5 mt-6">
          <img
            src="/gamepad.png"
            alt="Arecotech"
            className="w-5 h-5 object-contain opacity-40"
          />
          <p className="text-slate-400 text-[11px] font-semibold uppercase tracking-widest">
            Powered by Arecotech
          </p>
        </div>

      </div>
    </div>
  );
}
